// components/common/GoogleAddressInput.tsx
import { useState, useCallback, useEffect } from "react";
import { Input } from "antd";
import { Autocomplete, useJsApiLoader } from "@react-google-maps/api";
import { useParams } from "react-router-dom";

/// <reference types="google.maps" />

const libraries: "places"[] = ["places"];

interface AddressDetails {
  formatted_address: string;
  latitude: string;
  longitude: string;
  place_id: string;
  name: string;
  display_name: string;
  country: string;
  state: string;
  district: string;
  city: string;
  street: string;
  postal_code: string;
  phone: string;
}

interface GoogleAddressInputProps {
  value?: string | AddressDetails;
  onChange?: (details?: AddressDetails) => void;
  placeholder?: string;
  onAddressSelect?: (address: string, lat?: number, lng?: number) => void;
  style?: React.CSSProperties;
  disabled?: boolean;
  className?: string;
  countryRestriction?: string;
  types?: string[];
}

const GoogleAddressInput: React.FC<GoogleAddressInputProps> = ({
  value,
  onChange,
  placeholder = "Enter address",
  disabled = false,
  className,
  countryRestriction,
  types = ["address"],
  style,
}) => {
  const { id } = useParams();
  const EditMode = !!id;
  const getDisplayValue = (
    val: string | AddressDetails | undefined
  ): string => {
    if (typeof val === "string") {
      return val;
    }
    if (val && typeof val === "object") {
      // Remove trailing period or extra whitespace from name
      const cleanedName = val?.name?.trim().replace(/\.$/, "") ?? "";
      const display = val?.display_name?.trim() ?? "";

      return `${cleanedName}, ${display}`.trim();
    }
    return "";
  };

  const [inputValue, setInputValue] = useState<string>(
    getDisplayValue(value) ?? ""
  );
  const [autocomplete, setAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null);
  const googleMapsApiKey = "AIzaSyBBfoUsiF8ZX5iopPppB9x5XRriRaALhiY";
  // Load Google Maps API
  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: googleMapsApiKey,
    libraries,
  });

  useEffect(() => {
    if (EditMode) {
      setInputValue(getDisplayValue(value) ?? "");
    }
  }, [value, EditMode]);

  // Handle autocomplete load
  const onAutocompleteLoad = useCallback(
    (autocompleteInstance: google.maps.places.Autocomplete) => {
      setAutocomplete(autocompleteInstance);
    },
    []
  );

  // Handle place selection
  const onPlaceChanged = useCallback(() => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();

      if (place.formatted_address) {
        const addressComponents = place.address_components;
        let city = "";
        let state = "";
        let country = "";
        let district = "";
        let postalCode = "";
        let street = "";
        let streetNumber = "";

        // Extract address components
        addressComponents?.forEach((component: any) => {
          const types = component.types;

          if (types.includes("locality")) {
            city = component.long_name;
          } else if (
            types.includes("sublocality_level_1") ||
            types.includes("sublocality")
          ) {
            if (!city) {
              city = component.long_name;
            }
          } else if (types.includes("administrative_area_level_1")) {
            state = component.long_name.toUpperCase();
          } else if (types.includes("administrative_area_level_2")) {
            district = component.long_name;
          } else if (types.includes("country")) {
            country = component.long_name;
          } else if (types.includes("postal_code")) {
            postalCode = component.long_name;
          } else if (types.includes("route")) {
            street = component.long_name;
          } else if (types.includes("street_number")) {
            streetNumber = component.long_name;
          }
        });

        // Combine street number and street name
        const fullStreet = streetNumber ? `${streetNumber} ${street}` : street;

        const addressDetails: AddressDetails = {
          formatted_address: place.formatted_address,
          latitude: place.geometry?.location?.lat()?.toString() ?? "",
          longitude: place.geometry?.location?.lng()?.toString() ?? "",
          place_id: place.place_id ?? "",
          name: place.name ?? "",
          display_name: place.formatted_address ?? "",
          country: country,
          state: state,
          district: district,
          city: city,
          street: fullStreet,
          postal_code: postalCode,
          phone:
            place.formatted_phone_number ??
            place.international_phone_number ??
            "",
        };

        onChange?.(addressDetails);
        const displayValue =
          `${place.name ?? ""}, ${place.formatted_address ?? ""}`.trim();
        console.log("displayValue", displayValue);
        setInputValue(displayValue);
      }
    }
  }, [autocomplete, onChange]);

  // Handle manual input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    if (newValue === "") {
      onChange?.(undefined);
    }
  };

  if (loadError) {
    return <Input placeholder="Error loading address service" disabled />;
  }

  if (!isLoaded) {
    return <Input placeholder="Loading address service..." disabled />;
  }

  const autocompleteOptions: google.maps.places.AutocompleteOptions = {
    types,
  };

  if (countryRestriction) {
    autocompleteOptions.componentRestrictions = { country: countryRestriction };
  }

  return (
    <Autocomplete
      onLoad={onAutocompleteLoad}
      onPlaceChanged={onPlaceChanged}
      options={autocompleteOptions}
    >
      <Input
        value={inputValue}
        onChange={handleInputChange}
        placeholder={placeholder}
        disabled={disabled}
        onClear={() => {
          setInputValue("");
          onChange?.(undefined);
        }}
        className={className}
        style={style}
      />
    </Autocomplete>
  );
};

export default GoogleAddressInput;
